import { Ionicons } from '@expo/vector-icons';
import { router } from 'expo-router';
import React, { useMemo } from 'react';
import { Pressable, StyleSheet, View, type ViewStyle } from 'react-native';

import { Text } from '@/components/ui';
import { useApi } from '@/hooks/use-api';
import { type ApprovalQueue, useWorkOrderQueue } from '@/hooks/use-approval-queue';
import { useLabels } from '@/hooks/use-data';
import { usePermissions } from '@/hooks/use-permissions';
import { useWorkOrderStats } from '@/hooks/use-work-order-stats';
import { UserRole } from '@/types/models';

/**
 * The four headline tiles on the v2 dashboard (#279).
 *
 * ── SCOPED TO THE VIEWER, NOT THE COMPANY ──
 *
 * A property manager's tiles count THEIR associations; accounting and admins see the
 * portfolio. A PM looking at a company-wide "1,153 work orders" learns nothing they can
 * act on, and the number would disagree with every list they open.
 *
 * ── THE OUTSTANDING TILE IS CONDITIONAL ──
 *
 * It is portfolio receivables, so it only renders for someone with the accounting flag,
 * and it sums only the associations in scope. Maintenance and contractors have no business
 * seeing money owed, and the accounting endpoints 404 for them anyway — an ungated tile
 * would render an error or a misleading zero. Which means the row is 3 or 4 tiles wide
 * depending on who is looking, and that is deliberate.
 */

interface Tile {
	key: string;
	value: string;
	label: string;
	sub: string;
	icon: keyof typeof Ionicons.glyphMap;
	tint: string;
	bg: string;
	href?: string;
}

export const StatTiles: React.FC<{ style?: ViewStyle }> = ({ style }) => {
	const { user } = useApi();

	/**
	 * Property managers get their own slice; everyone else the portfolio.
	 *
	 * Passed to the endpoint so the scoping happens in SQL rather than by filtering a
	 * company-wide payload the user should not have received in the first place.
	 */
	const isPm = user?.role === UserRole.PropertyManager;
	const pmScope = isPm ? user?.id : undefined;

	const { data: stats, isLoading } = useWorkOrderStats(pmScope);
	/**
	 * The queue tiles read the QUEUE hooks, not `stats.pendingApproval`.
	 *
	 * Two sources for one number is how the tile said 280 while the To Do panel beside it
	 * said 86 and the page said 0. The queue hook owns the scope decision; everything that
	 * shows a count reads it.
	 *
	 * One tile per queue the viewer can act on — accounting gets Needs Review, a manager
	 * gets Needs Approval, an admin gets both — gated the same way the sidebar rows are, so
	 * the dashboard and the drawer never disagree about which queues exist for someone.
	 */
	const { canViewReviewQueue, canViewApprovalQueue, canUseInbox } = usePermissions();
	const reviewQueue = useWorkOrderQueue('needs_review', { enabled: canViewReviewQueue });
	const approvalQueue = useWorkOrderQueue('pending_approval', { enabled: canViewApprovalQueue });

	/**
	 * Inbox items assigned to the viewer and still OPEN.
	 *
	 * Reuses the count the rail and the drawer badge already render — same `/messages/labels`
	 * call, same unfiled-only default, filtered server-side to `assigned_to = me AND status =
	 * 'open'` — so the three surfaces cannot report different numbers. That is the same rule
	 * the queue tiles above follow, and for the same reason.
	 */
	const { data: labelRegistry } = useLabels({ enabled: canUseInbox });
	const assignedToMe = labelRegistry?.assignedToMe ?? 0;

	const tiles = useMemo<Tile[]>(() => {
		const s = stats;
		const list: Tile[] = [
			{
				key: 'work-orders',
				/**
				 * OPEN work orders only, not every row ever created.
				 *
				 * The all-time total was ~1,150 here and told nobody anything: it is
				 * dominated by years of completed work. Open is the number you can act on,
				 * and it is what the donut beside it now counts, so the two always agree.
				 */
				value: String(s?.open ?? 0),
				label: 'Open Work Orders',
				sub: `${s?.inProgress ?? 0} in progress`,
				icon: 'clipboard-outline',
				tint: '#1976d2',
				bg: '#e3f2fd',
				href: '/work-orders',
			},
			{
				key: 'quotes',
				value: String(s?.quotes ?? 0),
				label: 'Quotes',
				sub: 'awaiting approval',
				icon: 'document-text-outline',
				tint: '#00897b',
				bg: '#e0f2f1',
				href: '/quotes',
			},
		];

		/**
		 * Label, caption and count all come from the queue hook, so a tile, its sidebar
		 * badge and its page cannot disagree.
		 *
		 * These no longer fold in `pendingItems` (work on hold for a part or a vendor): that
		 * conflated "somebody must act" with "we are blocked on a third party and there is
		 * nothing to do". The hold count lives on the Pending page, which only maintenance
		 * sees.
		 */
		const queueTile = (queue: ApprovalQueue, key: string, href: string, icon: Tile['icon']): Tile => ({
			key,
			value: String(queue.count),
			label: queue.label,
			sub: queue.caption,
			icon,
			tint: '#ef6c00',
			bg: '#fff3e0',
			href,
		});
		if (canViewReviewQueue) {
			list.push(queueTile(reviewQueue, 'review-queue', '/work-orders/needs-review', 'create-outline'));
		}
		if (canViewApprovalQueue) {
			list.push(queueTile(approvalQueue, 'approval-queue', '/work-orders/needs-approval', 'checkmark-circle-outline'));
		}

		/**
		 * Mail handed to this person specifically, and still open.
		 *
		 * Replaces the Outstanding-receivables tile that used to sit here. That number was a
		 * portfolio TOTAL — it moved when anyone posted a payment anywhere, nobody could act
		 * on it from the dashboard, and it was the same figure every day. This tile is work
		 * with the viewer's name on it, which is the only thing on this row they can clear.
		 * AR still lives on the accounting pages, where it is actionable.
		 *
		 * BLUE, matching the drawer badge and the rail row for the same count — "this is
		 * yours", which is not the claim red makes. It shipped red, next to Overdue, until
		 * someone pointed out that a personal queue with mail in it is the ordinary state of
		 * affairs; painting an ordinary Tuesday as an alarm is how people stop reading the
		 * alarms. Shown at zero too — a tile that vanishes once cleared makes people wonder
		 * where it went, and "nothing is waiting on me" is worth saying.
		 */
		if (canUseInbox) {
			list.push({
				key: 'assigned-to-me',
				value: String(assignedToMe),
				label: 'Assigned to Me',
				sub: assignedToMe === 0 ? 'nothing waiting on you' : 'inbox items',
				icon: 'person-circle-outline',
				tint: '#1976d2',
				bg: '#e3f2fd',
				href: '/inbox/mine',
			});
		}
		return list;
	}, [stats, reviewQueue, approvalQueue, canViewReviewQueue, canViewApprovalQueue, canUseInbox, assignedToMe]);

	return (
		<View style={[styles.row, style]} testID="v2-stat-tiles">
			{tiles.map((t) => (
				<Pressable
					key={t.key}
					onPress={t.href ? () => router.push(t.href as never) : undefined}
					style={styles.tile}
					testID={`v2-tile-${t.key}`}
				>
					<View style={[styles.iconWrap, { backgroundColor: t.bg }]}>
						<Ionicons name={t.icon} size={22} color={t.tint} />
					</View>
					<View style={styles.tileText}>
						{/* Em dash while loading, never a 0 — a placeholder zero reads as real. */}
						<Text style={styles.value}>{isLoading ? '—' : t.value}</Text>
						<Text style={styles.label}>{t.label}</Text>
						<Text style={styles.sub}>{t.sub}</Text>
					</View>
				</Pressable>
			))}
		</View>
	);
};

const styles = StyleSheet.create({
	row: { flexDirection: 'row', gap: 14, flexWrap: 'wrap' },
	tile: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: 14,
		// Grows to fill the row but never below a legible width, so four tiles wrap to two
		// rows on a narrow window instead of crushing.
		flexGrow: 1,
		flexBasis: 220,
		minWidth: 0,
		backgroundColor: '#fff',
		borderRadius: 12,
		borderWidth: 1,
		borderColor: '#e8eaed',
		paddingVertical: 18,
		paddingHorizontal: 18,
	},
	iconWrap: {
		width: 46, height: 46, borderRadius: 23,
		alignItems: 'center', justifyContent: 'center',
	},
	tileText: { flex: 1, minWidth: 0 },
	value: { fontSize: 24, fontWeight: '700', color: '#1a2027' },
	label: { fontSize: 14, fontWeight: '600', color: '#37474f', marginTop: 1 },
	sub: { fontSize: 12, color: '#90a4ae', marginTop: 2 },
});
